import { MapPin, Phone, Mail } from "lucide-react";
import ContactForm from "@/components/ContactForm";
import useReveal from "@/hooks/useReveal";

const ADDRESS_LINE_1 = "Piazza dei Martiri 30";
const ADDRESS_LINE_2 = "Napoli, 80121 (NA)";
const PHONE_DISPLAY = "+39 344 757 4610";
const PHONE_WA = "393447574610";
const WHATSAPP_URL = `https://wa.me/${PHONE_WA}?text=${encodeURIComponent("Ciao, vorrei informazioni sui vostri servizi")}`;
const EMAIL = "info@bvbusinessconsulting.it";

const MAPS_QUERY = "Piazza+dei+Martiri+30,+Napoli";
const MAPS_EMBED = `https://maps.google.com/maps?q=${MAPS_QUERY}&t=&z=16&ie=UTF8&iwloc=&output=embed`;

// Hero banner image for /contatti
const HERO_BANNER = "/contact-hero.jpg";

export default function Contact() {
  useReveal();
  return (
    <div data-testid="page-contact">
      {/* BLOCCO A — Hero introduttivo con immagine banner */}
      <section
        className="relative overflow-hidden bg-[#0E2E4D] text-white"
        data-testid="contact-hero"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_BANNER}
            alt="BV Business Consulting team"
            className="w-full h-full object-cover opacity-25"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(120deg, rgba(14,46,77,0.92) 0%, rgba(14,46,77,0.65) 60%, rgba(14,46,77,0.5) 100%)",
            }}
          />
        </div>
        <div className="relative bv-container py-20 md:py-28">
          <p className="overline reveal" style={{ color: "#36BFC4" }} data-testid="contact-hero-overline">
            BV Business Consulting
          </p>
          <h1
            data-testid="contact-hero-title"
            className="h-display text-3xl sm:text-4xl md:text-5xl mt-6 leading-[1.05] max-w-[40ch] reveal text-white"
          >
            Ti forniamo <span style={{ color: "#36BFC4" }}>soluzioni su misura</span> invece di una semplice
            consulenza generica, adattandoci alle tue esigenze specifiche.
          </h1>
        </div>
      </section>

      {/* BLOCCO B — METTITI IN CONTATTO */}
      <section className="bv-container pt-20 md:pt-28 pb-24 md:pb-32" data-testid="contact-main">
        <div className="reveal">
          <p className="overline" data-testid="contact-section-overline">/ Mettiti in contatto</p>
          <h2
            data-testid="contact-section-title"
            className="h-display text-4xl md:text-6xl mt-5 leading-[0.98]"
          >
            Contattaci per <span style={{ color: "#2BA8AD" }}>informazioni</span>.
          </h2>
          <p
            data-testid="contact-section-sub"
            className="mt-6 max-w-2xl text-[16px] md:text-[17px] text-[#0E2E4D]/75"
          >
            Mandaci un messaggio per informazioni o prenota un appuntamento per una consulenza.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-12 gap-10 lg:gap-14">
          {/* COLONNA SINISTRA */}
          <div className="col-span-12 lg:col-span-5">
            <ul className="space-y-1">
              <InfoBlock
                testid="contact-info-address"
                icon={<MapPin size={22} strokeWidth={1.6} />}
                title="Il nostro indirizzo"
              >
                <p className="text-[15px] text-[#0E2E4D]">{ADDRESS_LINE_1}</p>
                <p className="text-[15px] text-[#0E2E4D]">{ADDRESS_LINE_2}</p>
              </InfoBlock>

              <InfoBlock
                testid="contact-info-phone"
                icon={<Phone size={22} strokeWidth={1.6} />}
                title="WhatsApp"
              >
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-phone-link"
                  className="text-[15px] text-[#0E2E4D] link-underline"
                >
                  {PHONE_DISPLAY}
                </a>
              </InfoBlock>

              <InfoBlock
                testid="contact-info-email"
                icon={<Mail size={22} strokeWidth={1.6} />}
                title="Indirizzo e-mail"
              >
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="contact-email-link"
                  className="text-[15px] text-[#0E2E4D] link-underline break-all"
                >
                  {EMAIL}
                </a>
              </InfoBlock>
            </ul>

            {/* Mappa sotto i dati */}
            <div
              className="mt-10 border border-[#0E2E4D]/15 overflow-hidden reveal"
              data-testid="contact-mini-map"
              style={{ position: "relative", pointerEvents: "none" }}
            >
              <iframe
                title="Mappa BV Business Consulting"
                src={MAPS_EMBED}
                width="100%"
                height="320"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0, display: "block", pointerEvents: "none", touchAction: "pan-y" }}
              />
            </div>
          </div>

          {/* COLONNA DESTRA — Form multi-step */}
          <div
            className="col-span-12 lg:col-span-7 reveal"
            style={{ "--reveal-delay": "100ms" }}
          >
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Banda navy decorativa prima del footer */}
      <section
        data-testid="contact-bottom-band"
        className="bv-network text-white"
        aria-hidden="true"
      >
        <div className="bv-container py-10 md:py-12" />
      </section>
    </div>
  );
}

function InfoBlock({ testid, icon, title, children }) {
  return (
    <li
      data-testid={testid}
      className="flex items-start gap-5 py-5 border-b border-[#0E2E4D]/10 reveal"
    >
      <span
        className="inline-flex items-center justify-center w-12 h-12 border-2 rounded-full shrink-0"
        style={{ borderColor: "#2BA8AD", color: "#2BA8AD" }}
      >
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#0E2E4D]/55">{title}</p>
        <div className="mt-1.5 space-y-0.5">{children}</div>
      </div>
    </li>
  );
}
